// src/components/dashboard/DashboardLayout.tsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  Avatar,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  Chip,
  useMediaQuery,
  useTheme,
  SxProps,
  Theme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ShoppingBagOutlined as ShoppingBagIcon,
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  LogoutOutlined as LogoutIcon,
  DarkModeOutlined as DarkModeOutlinedIcon,
  LightModeOutlined as LightModeOutlinedIcon,
  MoreHoriz as MoreHorizIcon,
  LockOutlined as LockIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { MODULE_ICONS } from '../../constants/module-icons';
import { useColorMode } from '../../context/ColorModeContext';
import { useAppDispatch } from '../../redux/hook';
import { getAuthRoleName, logout } from '../../redux/slices/auth.slice';
import { useNavigationPermissions } from '../../hooks/useNavigationPermissions';
import { usePermission } from '../../hooks/usePermission';
import { SYSTEM_PERMISSIONS } from '../../constants/permissions.constants';
import { useSalesModal } from '../../hooks/useSalesModal';
import SalesModal from '../sales/SalesModal';
import CloseAllTabsButton from './CloseAllTabsButton';
import TabWorkspaceHost from './TabWorkspaceHost';
import ChangePasswordDialog from './ChangePasswordDialog';
import { ACTIVE_TABS_STORAGE_KEY, clearActiveTabsSession } from '../../utils/dashboardTabsSession';
import { ScrollableBox } from '../ui/ScrollableBox';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { LocationsService } from '../../api';
import villaImperialLogo from '../../assets/brand/villa-imperial-logo.png';

const DRAWER_WIDTH_EXPANDED = 130; // sidebar compacto expandido
const DRAWER_WIDTH_COLLAPSED = 52; // rail solo iconos
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'dashboardSidebarCollapsed';
const SIDEBAR_TEXT_FADE_MS = 80; // solo opacity — GPU-friendly
const SIDEBAR_PANEL_SLIDE_MS = 120; // transform al expandir (cosmético)

/** Pestañas visibles en barra; el resto en menú (mejor con zoom y muchas rutas abiertas). */
const MAX_INLINE_DASHBOARD_TABS = 6;
const TAB_CHIP_GAP_PX = 5;
const TAB_CHIP_BASE_PX = 76;
const TAB_CHIP_CHAR_PX = 7.2;
const TAB_CHIP_MIN_PX = 110;
const TAB_CHIP_MAX_PX = 280;
const RESERVED_OVERFLOW_BUTTON_PX = 44;

// Definir interfaz para las pestañas
interface TabInfo {
  id: string;
  label: string;
  path: string;
}

/**
 * Campanario del header: desactivado hasta tener API de notificaciones.
 * Cambiar a `true` para mostrar de nuevo el menú de muestra.
 */
const SHOW_DASHBOARD_NOTIFICATIONS = false;

/** Oculto hasta que el módulo Sistema tenga menús asignados. */
const SHOW_SYSTEM_NAV_ITEM = false;

/** Cambio de contraseña voluntario desde el menú de usuario. */
const SHOW_VOLUNTARY_CHANGE_PASSWORD = false;

/** Página de perfil de usuario (menú del avatar). */
const SHOW_USER_PROFILE_MENU_ITEM = false;

/** Scroll en submenú solo cuando hay muchos ítems (evita parpadeo de scrollbar). */
const SUBMENU_SCROLL_ITEM_THRESHOLD = 6;
const SUBMENU_SCROLL_MAX_HEIGHT = 280;

const STAR_POSITIONS = Array.from({ length: 20 }, () => ({
  top: Math.random() * 100,
  left: Math.random() * 100,
  duration: Math.random() * 5 + 3,
}));

const DASHBOARD_LAYOUT_KEYFRAMES = `
  @keyframes twinkle {
    0% { opacity: 0.2; }
    50% { opacity: 0.7; }
    100% { opacity: 0.2; }
  }
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes slideInRight {
    from { opacity: 0; transform: translateX(-8px) scale(0.98); }
    to { opacity: 1; transform: translateX(0) scale(1); }
  }
  @keyframes fadeInSubmenu {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const DashboardHeaderNotifications: React.FC = () => {
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<null | HTMLElement>(null);
  const handleNotificationsClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(event.currentTarget);
  };
  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };
  const notificationsOpen = Boolean(notificationsAnchorEl);
  const notifications = [
    { id: 1, message: 'Nueva venta registrada', time: '5 min' },
    { id: 2, message: 'Producto con stock bajo', time: '30 min' },
    { id: 3, message: 'Actualización de sistema disponible', time: '1 hora' },
  ];

  return (
    <Box sx={{ position: 'relative' }}>
      <IconButton
        size="small"
        onClick={handleNotificationsClick}
        aria-controls={notificationsOpen ? 'notifications-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={notificationsOpen ? 'true' : undefined}
        sx={{
          transition: 'all 0.3s',
          '&:hover': {
            transform: 'scale(1.1)',
          },
        }}
      >
        <Badge badgeContent={3} color="error">
          <NotificationsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        </Badge>
      </IconButton>

      <Menu
        id="notifications-menu"
        anchorEl={notificationsAnchorEl}
        open={notificationsOpen}
        onClose={handleNotificationsClose}
        MenuListProps={{
          'aria-labelledby': 'notifications-button',
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            width: 320,
            maxHeight: 480,
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            px: 2,
            py: 1,
            fontWeight: 500,
          }}
        >
          Notificaciones
        </Typography>
        <Divider />

        {notifications.map((notification, index) => (
          <MenuItem
            key={notification.id}
            onClick={handleNotificationsClose}
            sx={{
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: 'white',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              },
              animationDelay: `${index * 100}ms`,
              animation: 'slideDown 0.3s ease-out forwards',
            }}
          >
            <Box sx={{ py: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {notification.message}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Hace {notification.time}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        <Divider />
        <MenuItem
          onClick={handleNotificationsClose}
          sx={{
            justifyContent: 'center',
            transition: 'all 0.2s',
            '&:hover': {
              backgroundColor: 'cyan.50',
            },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'cyan.600',
              fontWeight: 500,
            }}
          >
            Ver todas las notificaciones
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

/** Mantiene orden de apertura: si existe no se mueve, si no existe se agrega al final. */
const ensureTabInOpenOrder = (
  prev: TabInfo[],
  path: string,
  createTab: () => TabInfo,
): TabInfo[] => {
  const exists = prev.some((t) => t.path === path);
  if (exists) {
    return prev;
  }
  return [...prev, createTab()];
};

const estimateTabChipWidth = (label: string) =>
  Math.min(
    TAB_CHIP_MAX_PX,
    Math.max(TAB_CHIP_MIN_PX, TAB_CHIP_BASE_PX + label.length * TAB_CHIP_CHAR_PX),
  );

type NavItem = {
  label: string;
  path: string;
  icon?: React.ReactNode;
  subItems?: NavItem[];
};

const DashboardLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { mode, toggleColorMode } = useColorMode();
  const canSelectTheme = usePermission(SYSTEM_PERMISSIONS.SELECT_THEME);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { openModal } = useSalesModal();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [panelSlideOffset, setPanelSlideOffset] = useState(0);
  const wasSidebarCollapsedRef = useRef(sidebarCollapsed);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [tabsOverflowAnchorEl, setTabsOverflowAnchorEl] = useState<null | HTMLElement>(null);
  const [activeTabs, setActiveTabs] = useState<TabInfo[]>([]);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverContent, setPopoverContent] = useState<NavItem | null>(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const tabsScrollRef = useRef<HTMLDivElement | null>(null);
  const tabChipRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [tabsViewportWidth, setTabsViewportWidth] = useState<number>(0);
  const [canScrollTabsLeft, setCanScrollTabsLeft] = useState(false);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);

  const isSidebarCollapsed = !isMobile && sidebarCollapsed;
  const currentDrawerWidth = isSidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;
  const sidebarTextTransition = theme.transitions.create('opacity', {
    easing: theme.transitions.easing.easeOut,
    duration: SIDEBAR_TEXT_FADE_MS,
  });
  const sidebarPanelSlideTransition = theme.transitions.create('transform', {
    easing: 'cubic-bezier(0, 0, 0.2, 1)',
    duration: SIDEBAR_PANEL_SLIDE_MS,
  });

  // Handlers
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarCollapseToggle = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });
    handlePopoverClose();
  };

  useEffect(() => {
    if (isSidebarCollapsed) {
      setPopoverAnchorEl(null);
      setPopoverContent(null);
      setPanelSlideOffset(0);
    } else if (wasSidebarCollapsedRef.current) {
      setPanelSlideOffset(-10);
      const frame1 = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setPanelSlideOffset(0);
        });
      });
      return () => cancelAnimationFrame(frame1);
    }
    wasSidebarCollapsedRef.current = isSidebarCollapsed;
  }, [isSidebarCollapsed]);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleLogout = () => {
    setActiveTabs([]);
    clearActiveTabsSession();
    dispatch(logout());
    navigate('/login');
  };

  const handleTabClose = (tabId: string) => {
    // Eliminar la pestaña específica
    const updatedTabs = activeTabs.filter((tab) => tab.id !== tabId);
    setActiveTabs(updatedTabs);

    // Si cerramos la pestaña activa, navegamos a la última pestaña o al home
    if (location.pathname === tabId) {
      if (updatedTabs.length > 0) {
        navigate(updatedTabs[updatedTabs.length - 1].path);
      } else {
        navigate('/dashboard');
      }
    }
  };

  const handleTabMiddleClick = (e: React.MouseEvent, tabId: string, onAfterClose?: () => void) => {
    if (e.button !== 1) return;
    e.preventDefault();
    e.stopPropagation();
    handleTabClose(tabId);
    onAfterClose?.();
  };

  const handleCloseAllTabs = () => {
    setActiveTabs([]);
    setTabsOverflowAnchorEl(null);
    clearActiveTabsSession();
    navigate('/dashboard');
  };

  const { inlineTabs, overflowTabs } = useMemo(() => {
    if (activeTabs.length <= MAX_INLINE_DASHBOARD_TABS && tabsViewportWidth === 0) {
      return { inlineTabs: activeTabs, overflowTabs: [] as TabInfo[] };
    }

    const reserveRightControls = activeTabs.length > 1 ? RESERVED_OVERFLOW_BUTTON_PX : 0;
    const availableWidth = Math.max(0, tabsViewportWidth - reserveRightControls);

    if (availableWidth <= 0) {
      return { inlineTabs: activeTabs.slice(0, 1), overflowTabs: activeTabs.slice(1) };
    }

    const activeTabIndex = activeTabs.findIndex((tab) => tab.path === location.pathname);
    const maxCountFallback = activeTabs.slice(0, MAX_INLINE_DASHBOARD_TABS);
    let consumed = 0;
    const fitted: TabInfo[] = [];

    for (const tab of activeTabs) {
      const tabWidth = estimateTabChipWidth(tab.label) + TAB_CHIP_GAP_PX;
      if (fitted.length === 0 || consumed + tabWidth <= availableWidth) {
        fitted.push(tab);
        consumed += tabWidth;
      } else {
        break;
      }
    }

    let dynamicInlineTabs =
      fitted.length > 0 ? fitted : activeTabs.slice(0, Math.min(1, activeTabs.length));

    if (!dynamicInlineTabs.some((tab) => tab.path === location.pathname) && activeTabIndex >= 0) {
      const activeTab = activeTabs[activeTabIndex];
      if (dynamicInlineTabs.length === 0) {
        dynamicInlineTabs = [activeTab];
      } else {
        dynamicInlineTabs = [...dynamicInlineTabs.slice(0, -1), activeTab];
      }
    }

    // Fallback conservador para evitar demasiadas tabs inline en pantallas muy estrechas.
    if (dynamicInlineTabs.length > MAX_INLINE_DASHBOARD_TABS && tabsViewportWidth === 0) {
      dynamicInlineTabs = maxCountFallback;
    }

    const inlineIds = new Set(dynamicInlineTabs.map((tab) => tab.id));
    const dynamicOverflowTabs = activeTabs.filter((tab) => !inlineIds.has(tab.id));

    return { inlineTabs: dynamicInlineTabs, overflowTabs: dynamicOverflowTabs };
  }, [activeTabs, location.pathname, tabsViewportWidth]);

  useEffect(() => {
    const target = tabsScrollRef.current;
    if (!target) {
      return;
    }

    const updateWidth = () => setTabsViewportWidth(target.clientWidth);
    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(target);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const target = tabsScrollRef.current;
    if (!target) {
      return;
    }

    const updateScrollButtons = () => {
      const maxScrollLeft = Math.max(0, target.scrollWidth - target.clientWidth);
      setCanScrollTabsLeft(target.scrollLeft > 4);
      setCanScrollTabsRight(target.scrollLeft < maxScrollLeft - 4);
    };

    updateScrollButtons();
    target.addEventListener('scroll', updateScrollButtons, { passive: true });
    const observer = new ResizeObserver(updateScrollButtons);
    observer.observe(target);

    return () => {
      target.removeEventListener('scroll', updateScrollButtons);
      observer.disconnect();
    };
  }, [activeTabs, inlineTabs, isMobile]);

  useEffect(() => {
    const activeInlineTab = (isMobile ? inlineTabs : activeTabs).find(
      (tab) => tab.path === location.pathname,
    );
    if (!activeInlineTab) {
      return;
    }
    const activeChip = tabChipRefs.current[activeInlineTab.id];
    if (activeChip) {
      activeChip.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    }
  }, [inlineTabs, activeTabs, location.pathname, isMobile]);

  const handleScrollTabsBy = (delta: number) => {
    const target = tabsScrollRef.current;
    if (!target) {
      return;
    }
    target.scrollBy({ left: delta, behavior: 'smooth' });
  };

  const handlePopoverClose = () => {
    setPopoverAnchorEl(null);
    setPopoverContent(null);
  };

  const openSubmenuPopover = (anchor: HTMLElement, item: NavItem) => {
    const rect = anchor.getBoundingClientRect();
    setPopoverPosition({
      top: Math.max(10, rect.top - 4),
      left: rect.right + 8,
    });
    setPopoverContent(item);
    setPopoverAnchorEl(anchor);
  };

  const handleItemClick = (item: NavItem, event?: React.MouseEvent<HTMLElement>) => {
    if (item.subItems && item.subItems.length > 0) {
      if (event) {
        openSubmenuPopover(event.currentTarget, item);
      }
      return;
    }
    navigate(item.path);
    handlePopoverClose();
  };

  const handleSubmenuMouseEnter = (e: React.MouseEvent<HTMLElement>, item: NavItem) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    if (item.subItems?.length) {
      openSubmenuPopover(e.currentTarget, item);
    }
  };

  const handleSubmenuMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      handlePopoverClose();
    }, 150);
  };

  const handlePopoverEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handlePopoverLeave = () => {
    handlePopoverClose();
  };

  const handleSubItemClick = (subItem: NavItem) => {
    navigate(subItem.path);
    handlePopoverClose();
  };

  // Navigation permissions - Centralized in a single hook
  // This hook memoizes all navigation permission checks
  const navPerms = useNavigationPermissions();

  const navItems: NavItem[] = useMemo(() => {
    const items: NavItem[] = [];

    if (navPerms.canHome) {
      items.push({ label: 'Inicio', icon: MODULE_ICONS.home, path: '/dashboard' });
    }

    if (navPerms.canProducts) {
      items.push({
        label: 'Productos',
        icon: MODULE_ICONS.products,
        path: '/dashboard/products',
        subItems: [{ label: 'Catálogo de Productos', path: '/dashboard/products' }],
      });
    }

    const inventoryMenus: NavItem[] = [];
    if (navPerms.canInventoryRegister)
      inventoryMenus.push({
        label: 'Registro de Inventario',
        path: '/dashboard/inventory/register',
      });
    if (navPerms.canInventoryWarehouse)
      inventoryMenus.push({ label: 'Gestión en Almacén', path: '/dashboard/inventory/query' });
    if (navPerms.canInventoryTransfer)
      inventoryMenus.push({
        label: 'Transferencia de Inventario',
        path: '/dashboard/inventory/transfer',
      });
    if (navPerms.canInventoryRequest)
      inventoryMenus.push({
        label: 'Solicitudes de inventario',
        path: '/dashboard/inventory/requests',
      });
    if (navPerms.canInventoryBranch)
      inventoryMenus.push({
        label: 'Gestión en Sucursal',
        path: '/dashboard/inventory/branch-query',
      });
    if (navPerms.canInventoryGeneral)
      inventoryMenus.push({
        label: 'Inventario general',
        path: '/dashboard/inventory/general',
      });
    if (navPerms.canSafetyStock)
      inventoryMenus.push({
        label: 'Stock de seguridad',
        path: '/dashboard/inventory/safety-stock',
      });
    if (navPerms.canPhysicalCount)
      inventoryMenus.push({
        label: 'Control de Inventario Físico',
        path: '/dashboard/inventory/physical-count',
      });

    if (inventoryMenus.length > 0) {
      items.push({
        label: 'Inventario',
        icon: MODULE_ICONS.inventory,
        path: '/dashboard/inventory',
        subItems: inventoryMenus,
      });
    }

    const ordersMenus: NavItem[] = [];
    if (navPerms.canSales)
      ordersMenus.push({ label: 'Gestión de Ventas', path: '/dashboard/orders' });
    if (navPerms.canSalesByPiece)
      ordersMenus.push({
        label: 'Ventas por Pieza',
        path: '/dashboard/orders/sales-by-piece',
      });
    if (navPerms.canSellerManagement)
      ordersMenus.push({
        label: 'Gestión de Vendedores',
        path: '/dashboard/orders/sellers',
      });

    if (ordersMenus.length > 0) {
      items.push({
        label: 'Ventas',
        icon: MODULE_ICONS.orders,
        path: '/dashboard/orders',
        subItems: ordersMenus,
      });
    }

    const crmMenus: NavItem[] = [];
    if (navPerms.canCrmClientManagement) {
      crmMenus.push({ label: 'Gestión de Clientes', path: '/dashboard/crm/clients' });
    }
    if (crmMenus.length > 0) {
      items.push({
        label: 'Clientes',
        icon: MODULE_ICONS.crm,
        path: '/dashboard/crm/clients',
        subItems: crmMenus,
      });
    }

    const reportsMenus: NavItem[] = [];
    if (navPerms.canReportsProductHistory)
      reportsMenus.push({
        label: 'Historial de Movimientos',
        path: '/dashboard/reports/product-history',
      });
    if (navPerms.canReportsStatistics)
      reportsMenus.push({ label: 'Estadísticas', path: '/dashboard/reports/statistics' });

    if (reportsMenus.length > 0) {
      items.push({
        label: 'Reportes',
        icon: MODULE_ICONS.reports,
        path: '/dashboard/reports',
        subItems: reportsMenus,
      });
    }

    const settingsMenus: NavItem[] = [];
    if (navPerms.canSettingsProps)
      settingsMenus.push({ label: 'Propiedades', path: '/dashboard/settings/properties' });
    if (navPerms.canSettingsRoles)
      settingsMenus.push({ label: 'Gestión de Roles', path: '/dashboard/settings/roles' });
    if (navPerms.canSettingsUsers)
      settingsMenus.push({ label: 'Gestión de Usuarios', path: '/dashboard/settings/users' });

    if (settingsMenus.length > 0) {
      items.push({
        label: 'Ajustes',
        icon: MODULE_ICONS.settings,
        path: '/dashboard/settings',
        subItems: settingsMenus,
      });
    }

    if (SHOW_SYSTEM_NAV_ITEM && navPerms.canSystem) {
      items.push({ label: 'Sistema', icon: MODULE_ICONS.system, path: '/dashboard/system' });
    }

    return items;
  }, [navPerms]);

  // Cargar pestañas desde el almacenamiento local al inicio (PRIMERO)
  useEffect(() => {
    const savedTabs = sessionStorage.getItem(ACTIVE_TABS_STORAGE_KEY);
    if (savedTabs) {
      try {
        const parsedTabs = JSON.parse(savedTabs);
        if (Array.isArray(parsedTabs) && parsedTabs.length > 0) {
          setActiveTabs(parsedTabs);
        }
      } catch (error) {
        console.error('Error al cargar pestañas guardadas', error);
      }
    }
  }, []); // Solo se ejecuta una vez al montar

  // Set active tab based on route (SEGUNDO - después de cargar desde sessionStorage)
  React.useEffect(() => {
    // Solo procesamos cuando hay un cambio de ruta y no es la ruta base del dashboard
    if (location.pathname !== '/dashboard') {
      const matchingNavItem = navItems.find(
        (item) =>
          item.path === location.pathname ||
          (item.subItems && item.subItems.some((subItem) => subItem.path === location.pathname)),
      );

      if (matchingNavItem) {
        // Determinar la etiqueta correcta
        let tabLabel = matchingNavItem.label;
        let tabPath = matchingNavItem.path;

        if (matchingNavItem.subItems) {
          const activeSubItem = matchingNavItem.subItems.find(
            (subItem) => subItem.path === location.pathname,
          );
          if (activeSubItem) {
            tabLabel = activeSubItem.label;
            tabPath = activeSubItem.path;
          }
        }

        setActiveTabs((prev) =>
          ensureTabInOpenOrder(prev, tabPath, () => ({
            id: tabPath,
            label: tabLabel,
            path: tabPath,
          })),
        );
      } else if (location.pathname.startsWith('/dashboard/')) {
        const pathParts = location.pathname.split('/');
        const lastPart = pathParts[pathParts.length - 1];
        const fallbackTabLabel = lastPart
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        setActiveTabs((prev) =>
          ensureTabInOpenOrder(prev, location.pathname, () => ({
            id: location.pathname,
            label: fallbackTabLabel,
            path: location.pathname,
          })),
        );
      }
    }
  }, [location.pathname, navItems]); // Agregar navItems a las dependencias

  // Guardar pestañas en almacenamiento local cuando cambian (F5 restaura; logout limpia)
  useEffect(() => {
    if (activeTabs.length > 0) {
      sessionStorage.setItem(ACTIVE_TABS_STORAGE_KEY, JSON.stringify(activeTabs));
    } else {
      clearActiveTabsSession();
    }
  }, [activeTabs]);

  /** Paths kept mounted in the workspace host (open chips + current route). */
  const workspaceAlivePaths = useMemo(() => {
    const paths = activeTabs.map((tab) => tab.path);
    if (!paths.includes(location.pathname)) {
      return [...paths, location.pathname];
    }
    return paths;
  }, [activeTabs, location.pathname]);

  // User menu
  const userMenuOpen = Boolean(userMenuAnchorEl);
  const authUser = useSelector((state: RootState) => state.auth.user);
  const [primaryLocationName, setPrimaryLocationName] = useState<string | null>(null);

  useEffect(() => {
    const locationId = authUser?.primaryLocationId;
    if (locationId == null) {
      setPrimaryLocationName(null);
      return;
    }

    let cancelled = false;
    LocationsService.locationsControllerFindAll()
      .then((response: unknown) => {
        if (cancelled) return;
        const raw = Array.isArray(response)
          ? response
          : ((response as { data?: unknown[] } | null)?.data ?? []);
        const match = (raw as Array<{ id?: number; name?: string }>).find(
          (loc) => Number(loc?.id) === Number(locationId),
        );
        setPrimaryLocationName(match?.name ? String(match.name) : null);
      })
      .catch(() => {
        if (!cancelled) setPrimaryLocationName(null);
      });

    return () => {
      cancelled = true;
    };
  }, [authUser?.primaryLocationId]);

  // Helper function to get display name
  const getUserDisplayName = (user: typeof authUser): string => {
    if (!user) return 'Usuario';

    // Try firstName + lastName first
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }

    // Fallback to firstName only
    if (user.firstName) {
      return user.firstName;
    }

    // Fallback to email
    if (user.email) {
      return user.email.split('@')[0];
    }

    return 'Usuario';
  };

  // Helper function to get user initials
  const getUserInitials = (user: typeof authUser): string => {
    if (!user) return 'U';

    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }

    if (user.firstName) {
      return user.firstName[0].toUpperCase();
    }

    if (user.email) {
      const emailParts = user.email.split('@')[0];
      return emailParts.substring(0, 2).toUpperCase();
    }

    return 'U';
  };

  // Helper function to get role display name (role name only — never description)
  const getRoleDisplayName = (user: typeof authUser): string => {
    if (!user || !user.roles || user.roles.length === 0) {
      return 'Usuario';
    }

    const roleName = getAuthRoleName(user.roles[0]);
    return roleName?.trim() || 'Usuario';
  };

  // Derive user info from Redux state
  const userInfo = {
    name: getUserDisplayName(authUser),
    role: getRoleDisplayName(authUser),
    location: primaryLocationName ?? 'Sin sucursal',
    initials: getUserInitials(authUser),
  };

  type NavItemTooltipOptions = {
    placement?: 'top' | 'right';
    suppress?: boolean;
  };

  // Función reutilizable para renderizar menús con hover
  const wrapNavItem = (
    label: string,
    node: React.ReactElement,
    options?: NavItemTooltipOptions,
  ) => {
    if (!isSidebarCollapsed) {
      return node;
    }
    return (
      <Tooltip
        title={label}
        placement={options?.placement ?? 'right'}
        arrow
        disableInteractive
        leaveDelay={0}
        open={options?.suppress ? false : undefined}
        slotProps={{
          popper: {
            modifiers: [
              { name: 'offset', options: { offset: [0, options?.placement === 'top' ? -6 : 0] } },
            ],
          },
        }}
      >
        {node}
      </Tooltip>
    );
  };

  const navItemButtonSx: SxProps<Theme> = {
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    position: 'relative',
    px: isSidebarCollapsed ? 1 : 0.75,
    py: isSidebarCollapsed ? 1 : 0.75,
    minHeight: isSidebarCollapsed ? 40 : 52,
  };

  const navItemIconSx: SxProps<Theme> = {
    minWidth: 0,
    mr: 0,
    mb: isSidebarCollapsed ? 0 : 0.25,
    justifyContent: 'center',
  };

  const navItemLabelSx: SxProps<Theme> = {
    flex: '0 0 auto',
    minWidth: 0,
    m: 0,
    textAlign: 'center',
    transition: sidebarTextTransition,
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
    ...(isSidebarCollapsed
      ? {
          width: 0,
          height: 0,
          opacity: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
        }
      : {
          width: '100%',
          opacity: 1,
        }),
  };

  const navItemLabelTypographyProps = {
    fontSize: '0.75rem',
    fontWeight: 500,
    noWrap: true,
    textAlign: 'center' as const,
    lineHeight: 1.2,
  };

  const submenuChevronSx: SxProps<Theme> = {
    position: 'absolute',
    top: 4,
    right: 4,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: isSidebarCollapsed ? 0 : 12,
    height: isSidebarCollapsed ? 0 : 12,
    opacity: isSidebarCollapsed ? 0 : 0.75,
    overflow: 'hidden',
    transition: sidebarTextTransition,
    pointerEvents: 'none',
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  };

  const navItemHoverSx = {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: '#ffffff',
    '& .MuiListItemText-primary': {
      transform: 'scale(1.05)',
      fontWeight: 600,
    },
  };

  const renderNavItem = (item: NavItem, isActive: boolean) => {
    const hasSubmenu = Boolean(item.subItems?.length);
    const isPopoverOpen =
      hasSubmenu && Boolean(popoverAnchorEl) && popoverContent?.label === item.label;

    const listItemButton = (
      <ListItemButton
        onClick={(e) => handleItemClick(item, e)}
        sx={{
          borderRadius: '8px',
          color: isActive ? '#14b8a6' : '#cbd5e1',
          background: 'transparent',
          fontWeight: isActive ? 600 : 400,
          borderLeft: '3px solid transparent',
          my: 0.5,
          ...navItemButtonSx,
          transition: 'background-color 0.15s ease, color 0.15s ease',
          '& .MuiListItemText-primary': {
            transition: 'transform 0.2s ease, font-weight 0.2s ease',
            transformOrigin: 'center center',
          },
          '&:hover': {
            ...navItemHoverSx,
            borderLeft: '3px solid transparent',
          },
        }}
      >
        <ListItemIcon
          sx={{
            color: isActive ? '#14b8a6' : '#cbd5e1',
            ...navItemIconSx,
            transition: 'color 0.15s',
            '& .MuiSvgIcon-root': { fontSize: '1.125rem' },
            '.MuiListItemButton-root:hover &': {
              color: '#ffffff',
            },
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          sx={navItemLabelSx}
          primaryTypographyProps={{
            ...navItemLabelTypographyProps,
            fontWeight: isActive ? 600 : 500,
          }}
        />
        {hasSubmenu && (
          <Box sx={submenuChevronSx}>
            <ChevronRightIcon sx={{ fontSize: 12 }} />
          </Box>
        )}
      </ListItemButton>
    );

    const wrapped = wrapNavItem(item.label, listItemButton, {
      placement: hasSubmenu ? 'top' : 'right',
      suppress: isPopoverOpen,
    });

    if (!hasSubmenu) {
      return wrapped;
    }

    return (
      <Box
        onMouseEnter={(e) => handleSubmenuMouseEnter(e, item)}
        onMouseLeave={handleSubmenuMouseLeave}
        sx={{ display: 'block', position: 'relative' }}
      >
        {wrapped}
      </Box>
    );
  };

  // Drawer content
  const drawer = (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transform: `translateX(${panelSlideOffset}px)`,
        transition: isSidebarCollapsed ? 'none' : sidebarPanelSlideTransition,
        willChange: 'transform',
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          transform: 'none',
        },
        background:
          'linear-gradient(180deg, rgb(26, 54, 115) 0%, rgb(33, 57, 95) 50%, rgb(37, 67, 120) 100%)',
      }}
    >
      {/* Star background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
        }}
      >
        {STAR_POSITIONS.map((star, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              height: '4px',
              width: '4px',
              backgroundColor: 'white',
              borderRadius: '50%',
              opacity: 0.3,
              top: `${star.top}%`,
              left: `${star.left}%`,
              boxShadow: '0 0 3px #fff',
              animation: `twinkle ${star.duration}s ease-in-out infinite`,
            }}
          />
        ))}

        <Box
          component="svg"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            opacity: 0.2,
            width: '100%',
            height: '100%',
          }}
        >
          <line x1="20%" y1="20%" x2="30%" y2="30%" stroke="#50F0B4" strokeWidth="0.3" />
          <line x1="70%" y1="15%" x2="85%" y2="20%" stroke="#50F0B4" strokeWidth="0.3" />
          <line x1="35%" y1="45%" x2="50%" y2="40%" stroke="#50F0B4" strokeWidth="0.3" />
          <line x1="65%" y1="60%" x2="75%" y2="70%" stroke="#50F0B4" strokeWidth="0.3" />
          <line x1="25%" y1="80%" x2="40%" y2="90%" stroke="#50F0B4" strokeWidth="0.3" />
        </Box>
      </Box>

      {/* Mountains */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          zIndex: 0,
        }}
      >
        <Box
          component="svg"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 240 320"
          preserveAspectRatio="none"
          sx={{
            width: '100%',
            opacity: 0.3,
          }}
        >
          <path
            d="M0,250 L40,240 L80,255 L120,235 L160,250 L200,240 L240,255 L240,320 L0,320 Z"
            fill="#071a33"
          />
        </Box>
      </Box>

      {/* Logo */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: isSidebarCollapsed ? 1 : 1.5,
          px: isSidebarCollapsed ? 0.5 : 1,
          borderBottom: '1px solid',
          borderColor: 'rgba(30, 58, 138, 1)',
          minHeight: isSidebarCollapsed ? 44 : 56,
          zIndex: 10,
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={villaImperialLogo}
          alt="Villa Imperial"
          sx={{
            display: 'block',
            width: '100%',
            maxWidth: isSidebarCollapsed ? 40 : 108,
            height: 'auto',
            maxHeight: isSidebarCollapsed ? 32 : 44,
            objectFit: 'contain',
            objectPosition: 'center',
            transition: sidebarTextTransition,
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
            },
          }}
        />
      </Box>

      {/* Navigation */}
      <Box
        ref={sidebarRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          zIndex: 10,
          position: 'relative',
          p: isSidebarCollapsed ? 1 : 1.5,
        }}
      >
        <List disablePadding>
          {wrapNavItem(
            'Nueva Venta',
            <ListItem
              component="button"
              onClick={openModal}
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                px: isSidebarCollapsed ? 1 : 0.75,
                py: isSidebarCollapsed ? 1 : 0.75,
                minHeight: isSidebarCollapsed ? 40 : 52,
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.03em',
                borderRadius: '16px',
                background: 'linear-gradient(180deg, #c4b5fd 0%, #7c3aed 100%)',
                color: 'white',
                mb: 1,
                boxShadow:
                  'inset 0 1px 0 rgba(255, 255, 255, 0.35), 0 4px 14px rgba(124, 58, 237, 0.35)',
                transition:
                  'transform 0.2s cubic-bezier(0.25, 0.1, 0.25, 1), box-shadow 0.2s ease, background 0.2s ease',
                width: '100%',
                border: 'none',
                cursor: 'pointer',
                fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', Inter, sans-serif",
                '&:hover': {
                  background: 'linear-gradient(180deg, #a78bfa 0%, #6d28d9 100%)',
                  boxShadow:
                    'inset 0 1px 0 rgba(255, 255, 255, 0.4), 0 6px 20px rgba(124, 58, 237, 0.45)',
                },
                '&:active': {
                  transform: 'scale(0.97)',
                  boxShadow:
                    'inset 0 1px 0 rgba(255, 255, 255, 0.25), 0 2px 8px rgba(124, 58, 237, 0.3)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: 0,
                  mb: isSidebarCollapsed ? 0 : 0.25,
                  color: 'white',
                  justifyContent: 'center',
                }}
              >
                <ShoppingBagIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Venta"
                sx={navItemLabelSx}
                primaryTypographyProps={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  noWrap: true,
                  textAlign: 'center',
                  lineHeight: 1.2,
                  color: 'white',
                }}
              />
            </ListItem>,
          )}

          {navItems.map((item) => {
            const isActive =
              location.pathname === item.path ||
              Boolean(item.subItems?.some((subItem) => subItem.path === location.pathname));

            return (
              <React.Fragment key={item.label}>{renderNavItem(item, isActive)}</React.Fragment>
            );
          })}
        </List>
      </Box>

      {/* Collapse toggle — bottom of sidebar */}
      {!isMobile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            py: 1,
            px: isSidebarCollapsed ? 0.5 : 1,
            borderTop: '1px solid',
            borderColor: 'rgba(30, 58, 138, 1)',
            zIndex: 10,
            position: 'relative',
          }}
        >
          <Tooltip
            title={isSidebarCollapsed ? 'Expandir menú' : 'Colapsar menú'}
            placement="right"
            arrow
          >
            <IconButton
              size="small"
              onClick={handleSidebarCollapseToggle}
              aria-label={isSidebarCollapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
              sx={{
                color: 'rgba(255, 255, 255, 0.85)',
                flexShrink: 0,
                p: 0.5,
                '&:hover': {
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              {isSidebarCollapsed ? (
                <ChevronRightIcon sx={{ fontSize: '1.25rem' }} />
              ) : (
                <ChevronLeftIcon sx={{ fontSize: '1.125rem' }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );

  const renderSubmenuPopover = () => {
    if (!popoverAnchorEl || !popoverContent?.subItems?.length) {
      return null;
    }

    const subItems = popoverContent.subItems;
    const submenuNeedsScroll = subItems.length > SUBMENU_SCROLL_ITEM_THRESHOLD;

    return createPortal(
      <Box
        sx={{
          position: 'fixed',
          top: `${popoverPosition.top}px`,
          left: `${popoverPosition.left}px`,
          zIndex: 1300,
          background:
            'linear-gradient(135deg,rgb(46, 61, 109) 0%,rgb(46, 79, 133) 50%,rgb(27, 52, 120) 100%)',
          borderRadius: '8px',
          minWidth: 160,
          maxWidth: 200,
          overflow: 'hidden',
          boxShadow: '0 8px 20px -4px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.06)',
          animation: 'slideInRight 0.15s cubic-bezier(0.25, 0.8, 0.25, 1) forwards',
          transformOrigin: 'left center',
        }}
        onMouseEnter={handlePopoverEnter}
        onMouseLeave={handlePopoverLeave}
      >
        <ScrollableBox
          variant="onDark"
          axis="y"
          stableGutter={submenuNeedsScroll}
          sx={{
            py: 0.5,
            overflowY: submenuNeedsScroll ? 'auto' : 'hidden',
            ...(submenuNeedsScroll && { maxHeight: SUBMENU_SCROLL_MAX_HEIGHT }),
          }}
        >
          {subItems.map((subItem, index) => {
            const isSubActive = location.pathname === subItem.path;
            return (
              <ListItemButton
                key={subItem.label}
                onClick={() => handleSubItemClick(subItem)}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  mx: 0.25,
                  borderRadius: '8px',
                  minHeight: 28,
                  overflow: 'hidden',
                  color: isSubActive ? '#ffffff' : '#cbd5e1',
                  fontWeight: isSubActive ? 600 : 500,
                  fontSize: '0.8125rem',
                  lineHeight: 1.25,
                  opacity: 0,
                  animation: `fadeInSubmenu 0.2s ease-out ${index * 30}ms forwards`,
                  transition:
                    'background-color 0.15s ease, color 0.15s ease, padding-left 0.15s ease',
                  '& .MuiListItemText-primary': {
                    transition: 'transform 0.2s ease, font-weight 0.2s ease',
                    transformOrigin: 'left center',
                  },
                  '&:hover': {
                    ...navItemHoverSx,
                    pl: 1.75,
                  },
                }}
              >
                <ListItemText
                  primary={subItem.label}
                  primaryTypographyProps={{
                    fontSize: '0.8125rem',
                    fontWeight: isSubActive ? 600 : 500,
                    noWrap: true,
                    lineHeight: 1.25,
                  }}
                />
              </ListItemButton>
            );
          })}
        </ScrollableBox>
      </Box>,
      document.body,
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        backgroundColor: 'background.default',
      }}
    >
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: { md: currentDrawerWidth },
          flexShrink: { md: 0 },
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH_EXPANDED,
              background: 'none',
              boxShadow: 'none',
              border: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              overflowX: 'hidden',
              background: 'none',
              boxShadow: 'none',
              border: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header with profile and tab */}
        <AppBar
          position="static"
          color="transparent"
          elevation={0}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: mode === 'dark' ? 'background.paper' : 'common.white',
          }}
        >
          <Toolbar
            sx={{
              minHeight: 0,
              height: 'auto',
              px: 2,
              pt: 1,
              pb: 0,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
            }}
          >
            {/* Mobile menu button */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            {/* Pestañas: scroll solo en chips; menú overflow y cerrar todas siempre accesibles */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'flex-end',
                gap: 1,
                pb: 0,
              }}
            >
              <ScrollableBox
                ref={tabsScrollRef}
                variant="hidden"
                axis="x"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  position: 'relative',
                  alignItems: 'flex-end',
                  gap: '5px',
                }}
              >
                {(isMobile ? inlineTabs : activeTabs).map((tab, index) => (
                  <Chip
                    ref={(el: HTMLDivElement | null) => {
                      tabChipRefs.current[tab.id] = el;
                    }}
                    key={tab.id}
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <span>{tab.label}</span>
                        <CloseIcon
                          fontSize="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTabClose(tab.id);
                          }}
                          sx={{
                            borderRadius: '50%',
                            p: 0.5,
                            transition: 'transform 0.075s',
                            '&:hover': {
                              transform: 'scale(1.25)',
                            },
                          }}
                        />
                      </Box>
                    }
                    onClick={() => navigate(tab.path)}
                    onAuxClick={(e) => handleTabMiddleClick(e, tab.id)}
                    onMouseDown={(e) => {
                      if (e.button === 1) e.preventDefault();
                    }}
                    sx={{
                      px: 2,
                      py: 1.5,
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'all 0.1s',
                      whiteSpace: 'nowrap',
                      minHeight: '40px',
                      height: 'auto',
                      mb: 0,
                      borderRadius: '6px 6px 0 0 !important',
                      ...(location.pathname === tab.path
                        ? {
                            color: 'white',
                            background:
                              'linear-gradient(to right, rgb(6, 182, 212), rgb(16, 185, 129))',
                            boxShadow: 3,
                            borderTop: '4px solid',
                            borderLeft: '4px solid',
                            borderRight: '4px solid',
                            borderBottom: 'none',
                            borderColor: 'rgb(8, 145, 178)',
                            position: 'relative',
                            zIndex: 10,
                            '&.MuiChip-root': {
                              border: 'none !important',
                            },
                          }
                        : {
                            color: 'text.primary',
                            backgroundColor: mode === 'dark' ? 'background.paper' : 'common.white',
                            border: '1px solid',
                            borderBottom: 'none',
                            borderColor: 'divider',
                            boxShadow: 'none',
                            '&:hover': {
                              backgroundColor: mode === 'dark' ? 'action.hover' : 'common.white',
                              borderColor: 'action.disabled',
                            },
                            '&.MuiChip-root': {
                              borderStyle: 'solid',
                              borderBottomStyle: 'none',
                            },
                          }),
                      '& .MuiChip-label': {
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                      },
                      animationDelay: `${index * 50}ms`,
                      animation: 'slideDown 0.2s ease-out forwards',
                    }}
                  />
                ))}
              </ScrollableBox>

              {!isMobile && activeTabs.length > 0 && (
                <>
                  <IconButton
                    size="small"
                    onClick={() => handleScrollTabsBy(-220)}
                    disabled={!canScrollTabsLeft}
                    aria-label="Desplazar pestañas a la izquierda"
                    sx={{
                      flexShrink: 0,
                      borderRadius: '6px',
                      color: 'text.secondary',
                      '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                    }}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleScrollTabsBy(220)}
                    disabled={!canScrollTabsRight}
                    aria-label="Desplazar pestañas a la derecha"
                    sx={{
                      flexShrink: 0,
                      borderRadius: '6px',
                      color: 'text.secondary',
                      '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                    }}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </>
              )}

              {isMobile && overflowTabs.length > 0 && (
                <>
                  <Tooltip title={`Más pestañas (${overflowTabs.length})`} arrow>
                    <Badge
                      badgeContent={overflowTabs.length}
                      color="primary"
                      overlap="circular"
                      sx={{ flexShrink: 0 }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => setTabsOverflowAnchorEl(e.currentTarget)}
                        aria-label={`Ver ${overflowTabs.length} pestañas más`}
                        sx={{
                          color: 'text.secondary',
                          borderRadius: '6px',
                          '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                        }}
                      >
                        <MoreHorizIcon fontSize="small" />
                      </IconButton>
                    </Badge>
                  </Tooltip>
                  <Menu
                    anchorEl={tabsOverflowAnchorEl}
                    open={Boolean(tabsOverflowAnchorEl)}
                    onClose={() => setTabsOverflowAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                      paper: { sx: { maxHeight: 360, minWidth: 280 } },
                    }}
                  >
                    {overflowTabs.map((tab) => (
                      <MenuItem
                        key={tab.id}
                        selected={location.pathname === tab.path}
                        onClick={() => {
                          navigate(tab.path);
                          setTabsOverflowAnchorEl(null);
                        }}
                        onAuxClick={(e) =>
                          handleTabMiddleClick(e, tab.id, () => setTabsOverflowAnchorEl(null))
                        }
                        onMouseDown={(e) => {
                          if (e.button === 1) e.preventDefault();
                        }}
                        sx={{ py: 1, pr: 1, gap: 1 }}
                      >
                        <ListItemText
                          primary={tab.label}
                          primaryTypographyProps={{ noWrap: true }}
                          sx={{ flex: '1 1 auto', minWidth: 0, mr: 0.5 }}
                        />
                        <IconButton
                          size="small"
                          edge="end"
                          aria-label={`Cerrar pestaña ${tab.label}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTabClose(tab.id);
                            setTabsOverflowAnchorEl(null);
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Box>

            {/* User profile */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                alignSelf: 'flex-end',
              }}
            >
              {activeTabs.length >= 3 && <CloseAllTabsButton onCloseAll={handleCloseAllTabs} />}

              {canSelectTheme && (
                <Tooltip title={mode === 'dark' ? 'Modo claro' : 'Modo oscuro'}>
                  <IconButton
                    size="small"
                    onClick={toggleColorMode}
                    aria-label={mode === 'dark' ? 'Activar modo claro' : 'Activar modo oscuro'}
                    sx={{
                      transition: 'all 0.3s',
                      color: 'text.secondary',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        color: 'text.primary',
                      },
                    }}
                  >
                    {mode === 'dark' ? (
                      <LightModeOutlinedIcon fontSize="small" />
                    ) : (
                      <DarkModeOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}

              {SHOW_DASHBOARD_NOTIFICATIONS && <DashboardHeaderNotifications />}

              {/* User menu */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title="Perfil de usuario">
                  <IconButton
                    onClick={handleUserMenuClick}
                    size="small"
                    aria-controls={userMenuOpen ? 'user-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={userMenuOpen ? 'true' : undefined}
                    sx={{
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'scale(1.05)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar
                        sx={{
                          backgroundColor: 'rgb(30, 58, 138)',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          height: '24px',
                          width: '24px',
                          transition: 'all 0.3s',
                          '&:hover': {
                            boxShadow: 3,
                          },
                        }}
                        alt={userInfo.name}
                      >
                        {userInfo.initials}
                      </Avatar>
                      <Box sx={{ ml: 1, textAlign: 'left' }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 500,
                            color: 'text.primary',
                            display: 'block',
                            lineHeight: 1.2,
                          }}
                        >
                          {userInfo.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            display: 'block',
                            lineHeight: 1.2,
                          }}
                        >
                          {userInfo.role}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                            display: 'block',
                            lineHeight: 1.2,
                          }}
                        >
                          {userInfo.location}
                        </Typography>
                      </Box>
                    </Box>
                  </IconButton>
                </Tooltip>

                <Menu
                  id="user-menu"
                  anchorEl={userMenuAnchorEl}
                  open={userMenuOpen}
                  onClose={handleUserMenuClose}
                  MenuListProps={{
                    'aria-labelledby': 'user-button',
                  }}
                  PaperProps={{
                    elevation: 3,
                    sx: {
                      width: 200,
                      borderRadius: 2,
                      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                    },
                  }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  {SHOW_USER_PROFILE_MENU_ITEM && (
                    <MenuItem
                      onClick={handleUserMenuClose}
                      sx={{
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: 'white',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <SettingsIcon fontSize="small" />
                      </ListItemIcon>
                      <Typography variant="body2">Perfil</Typography>
                    </MenuItem>
                  )}
                  {SHOW_VOLUNTARY_CHANGE_PASSWORD && (
                    <MenuItem
                      onClick={() => {
                        handleUserMenuClose();
                        setChangePasswordOpen(true);
                      }}
                      sx={{
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: 'white',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                        },
                      }}
                    >
                      <ListItemIcon>
                        <LockIcon fontSize="small" />
                      </ListItemIcon>
                      <Typography variant="body2">Cambiar contraseña</Typography>
                    </MenuItem>
                  )}
                  {(SHOW_USER_PROFILE_MENU_ITEM || SHOW_VOLUNTARY_CHANGE_PASSWORD) && <Divider />}
                  <MenuItem
                    onClick={handleLogout}
                    sx={{
                      transition: 'all 0.2s',
                      color: 'error.main',
                      '&:hover': {
                        backgroundColor: 'error.50',
                      },
                    }}
                  >
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
                    </ListItemIcon>
                    <Typography variant="body2">Cerrar sesión</Typography>
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Main content: keep-alive workspace (scroll is per open tab panel) */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            px: 0, // Sin padding horizontal - PageContainer lo maneja
            py: 0, // Sin padding vertical - PageContainer lo maneja
            backgroundColor: 'transparent', // Permitir que el fondo del dashboard se vea
          }}
        >
          <TabWorkspaceHost alivePaths={workspaceAlivePaths} />
        </Box>
      </Box>
      <style>{DASHBOARD_LAYOUT_KEYFRAMES}</style>

      <SalesModal />

      {renderSubmenuPopover()}

      {SHOW_VOLUNTARY_CHANGE_PASSWORD && (
        <ChangePasswordDialog
          open={changePasswordOpen}
          onClose={() => setChangePasswordOpen(false)}
        />
      )}
    </Box>
  );
};
export default DashboardLayout;
